import React, { useEffect, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import AppLoader from '@projectK/components/AppLoader';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AddCardForm from './AddCardForm';
import AppGridContainer from '@projectK/components/AppGridContainer';
import CloseIcon from '@mui/icons-material/Close';
import AppCard from '@projectK/components/AppCard';
import { Fonts } from '@projectK/constants/AppEnums';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import IntlMessages from '@projectK/helpers/IntlMessages';

const brandLabel = (b) =>
  b ? String(b).charAt(0).toUpperCase() + String(b).slice(1) : 'Card';

const customScrollbarStyle = {
  // Define the styles for the scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461', // Lighter black color
    borderRadius: '8px', // Curved border
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#666', // Lighter black color on hover
  },
};

const addressValidationSchema = yup.object({
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .required(<IntlMessages id='validation.addressRequired' />),
  state: yup
    .string()
    .max(50, () => <IntlMessages id='validation.stateMax' />)
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.stateFormat' />
    ))
    .required(<IntlMessages id='validation.stateRequired' />),
  city: yup
    .string()
    .max(50, () => <IntlMessages id='validation.cityMax' />)
    .matches(/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.cityFormat' />
    ))
    .required(<IntlMessages id='validation.cityRequired' />),
  country: yup
    .string()
    .max(50, () => <IntlMessages id='validation.countryMax' />)
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.countryFormat' />
    ))
    .required(<IntlMessages id='validation.countryRequired' />),
  zip_code: yup
    .string()
    .matches(/^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, () => (
      <IntlMessages id='validation.zipCodeFormat' />
    ))
    .required(<IntlMessages id='validation.zipCodeRequired' />),
});

  const getColor = (companyTheme) => {
    return companyTheme?.primary?.main || '#2196f3';
  };

const MyWallet = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replaceDialog, setReplaceDialog] = useState({
    open: false,
    card: null,
    otherCards: [],
    replaceWithId: null,
  });
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({
    open: false,
    card: null,
  });
  const [editDialog, setEditDialog] = useState({
    open: false,
    card: null,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { showMessage, fetchError } = useInfoViewActionsContext();
  const companyTheme = CompanyTheme();
  const { user } = useAuthUser();
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [states, setStates] = useState([]);

  const fetchCards = async () => {
    try {
      const cardsRes = await jwtAxios.get(
        `/subscriptionsPayments/wallet/cards/${user.id}`,
      );
      setCards(cardsRes.data?.list || []);
    } catch (e) {
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);
  useEffect(() => {
    if (editDialog.country) {
      jwtAxios
        .get('/states', {
          params: { country: editDialog.country, perPage: 'all' },
        })
        .then((res) => setStates(res.data?.list ?? []))
        .catch(() => setStates([]));
    } else {
      setStates([]);
    }
  }, [editDialog.country]);
  const handleDeleteClick = (card) => {
    const hasActive = card.hasActiveSubscriptions === true;
    if (hasActive) {
      const otherCards = cards.filter((c) => c.id !== card.id);
      setReplaceDialog({
        open: true,
        card,
        otherCards,
        replaceWithId: otherCards.length > 0 ? otherCards[0].id : null,
      });
    } else {
      setConfirmDeleteDialog({ open: true, card });
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteDialog.card) return;
    setSubmitting(true);
    try {
      await jwtAxios.delete(
        `/subscriptionsPayments/wallet/cards/${confirmDeleteDialog.card.id}/${user.id}`,
      );
      showMessage('Card removed successfully');
      setConfirmDeleteDialog({ open: false, card: null });
      fetchCards();
    } catch (err) {
      fetchError(
        err?.response?.data?.error || err?.message || 'Failed to delete card',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplaceWithExisting = async () => {
    const { card, replaceWithId } = replaceDialog;
    if (!card || !replaceWithId) return;
    setSubmitting(true);
    try {
      await jwtAxios.post(
        `/subscriptionsPayments/wallet/cards/${card.id}/${user.id}/replace`,
        { newPaymentMethodId: replaceWithId },
      );
      showMessage('Card replaced and removed successfully');
      setReplaceDialog({
        open: false,
        card: null,
        otherCards: [],
        replaceWithId: null,
      });
      fetchCards();
    } catch (err) {
      fetchError(
        err?.response?.data?.error || err?.message || 'Failed to replace card',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (card) => {
    if (!card || card.is_default) return;
    setSubmitting(true);
    try {
      await jwtAxios.put(
        `/subscriptionsPayments/wallet/cards/${card.id}/${user.id}/default`,
      );
      showMessage('Default card updated');
      fetchCards();
    } catch (err) {
      fetchError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to set default card',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseReplace = () => {
    setReplaceDialog({
      open: false,
      card: null,
      otherCards: [],
      replaceWithId: null,
    });
  };

  const openEditDialog = (card) => {
    const addr = card.addressForForm ||
      (card.billing_details?.address && {
        address:
          [
            card.billing_details.address.line1,
            card.billing_details.address.line2,
          ]
            .filter(Boolean)
            .join(' ') || '',
        city: card.billing_details.address.city || '',
        state: card.billing_details.address.state || '',
        zip_code:
          card.billing_details.address.postal_code != null
            ? String(card.billing_details.address.postal_code)
            : '',
        country: card?.billing_details?.address?.country?.to || '',
      }) || { address: '', city: '', state: '', zip_code: '', country: '' };
    setEditDialog({
      open: true,
      card,
      address: addr.address ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      zip_code: addr.zip_code ?? '',
      country: addr.country?.toLowerCase() ?? '',
    });
  };

  const handleSaveEdit = async () => {
    const { card, address, city, state, zip_code, country } = editDialog;
    if (!card) return;
    setSubmitting(true);
    try {
      await addressValidationSchema.validate(
        { address, city, state, zip_code, country },
        { abortEarly: true },
      );
      await jwtAxios.patch(`/subscriptionsPayments/wallet/cards/${card.id}/${user.id}`, {
        address,
        city,
        state,
        zip_code,
        country,
      });
      showMessage('Card address updated successfully');
      setEditDialog({
        open: false,
        card: null,
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
      });
      fetchCards();
    } catch (err) {
      fetchError(
        err?.response?.data?.error ||
          err?.errors?.[0] ||
          err?.message ||
          'Failed to update address',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAddCardDialog = () => setAddCardOpen(true);

  if (loading) {
    return <AppLoader />;
  }
  const handleClose = () => {
    setEditDialog({
      open: false,
      card: null,
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
    });
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 2, justifyContent: 'space-between' }}>
        <Box>
          <Typography
            component='h3'
            sx={{
              fontSize: 16,
              fontWeight: Fonts.BOLD,
              mb: { xs: 3, lg: 5 },
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
          >
            My Wallet
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Manage your saved payment methods. You can remove a card if it is
            not linked to any active subscription.
          </Typography>
        </Box>
        <Box>
          <Button
            variant='outlined'
            startIcon={<AddCardOutlinedIcon />}
            onClick={openAddCardDialog}
            sx={{
              borderColor: getColor(companyTheme),
              color: getColor(companyTheme),
            }}
          >
            Add card
          </Button>
        </Box>
      </Box>
      {cards.length === 0 ? (
        <Card variant='outlined'>
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              color={alpha(
                getColor(companyTheme),
                0.8,
              )}
            >
              You have no saved cards. Add a card above or during checkout
              payment
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box
          className='my-wallet'
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <div className={`${cards.length > 2 && 'credit-cards'}`}>
            {cards.map((card) => (
              <Card key={card.id} style={{paddingBottom : 2}} variant='outlined'>
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CreditCardIcon color='action' />
                    <Box>
                      <Typography variant='subtitle1'>
                        {brandLabel(card.brand)} •••• {card.last4}
                        {card.is_default && (
                          <Typography
                            component='span'
                            sx={{
                              ml: 1,
                              fontSize: '0.75rem',
                              color: getColor(companyTheme),
                              fontWeight: 600,
                            }}
                          >
                            (Default)
                          </Typography>
                        )}
                      </Typography>
                      {card.billing_address && (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          display='block'
                        >
                          {card.billing_address}
                        </Typography>
                      )}
                      {card.hasActiveSubscriptions &&
                        card.subscriptionCount > 0 && (
                          <Typography variant='caption' color='text.secondary'>
                            Linked to {card.subscriptionCount} active
                            subscription
                            {card.subscriptionCount !== 1 ? 's' : ''}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                  <Box>
                    <Button
                      size='small'
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => openEditDialog(card)}
                      sx={{
                        color: getColor(companyTheme),
                      }}
                    >
                      Edit address
                    </Button>
                    {!card.is_default && (
                      <Button
                        size='small'
                        startIcon={<StarBorderIcon />}
                        onClick={() => handleSetDefault(card)}
                        disabled={submitting}
                        sx={{
                          color: getColor(companyTheme),
                        }}
                      >
                        Set as default
                      </Button>
                    )}
                    <Button
                      size='small'
                      color='error'
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleDeleteClick(card)}
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </div>
        </Box>
      )}

      <Dialog
        open={confirmDeleteDialog.open}
        onClose={() => setConfirmDeleteDialog({ open: false, card: null })}
      >
        <DialogTitle
          sx={{ color: getColor(companyTheme), fontSize: 'large' }}
        >
          Delete card
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this card?
            {confirmDeleteDialog.card && (
              <Typography component='span' fontWeight='medium'>
                {' '}
                {brandLabel(confirmDeleteDialog.card.brand)} ••••{' '}
                {confirmDeleteDialog.card.last4}
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => setConfirmDeleteDialog({ open: false, card: null })}
            sx={{
              position: 'relative',
              minWidth: 100,
              marginLeft: '10px',
              borderColor: getColor(companyTheme),
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: getColor(companyTheme),
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={handleConfirmDelete}
            sx={{
              backgroundColor: getColor(companyTheme),
              '&:hover': { backgroundColor: companyTheme?.primary?.dark },
            }}
            disabled={submitting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={replaceDialog.open}
        onClose={handleCloseReplace}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle
          sx={{ color: getColor(companyTheme), fontSize: 'large' }}
        >
          Card linked to active subscription(s)
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This card is currently linked to active subscription(s). To delete
            this card, you must replace it with another payment method.
          </Typography>
          {replaceDialog.otherCards.length > 0 ? (
            <RadioGroup
              value={replaceDialog.replaceWithId || ''}
              onChange={(e) =>
                setReplaceDialog((prev) => ({
                  ...prev,
                  replaceWithId: e.target.value,
                }))
              }
            >
              {replaceDialog.otherCards.map((c) => (
                <FormControlLabel
                  key={c.id}
                  value={c.id}
                  control={<Radio />}
                  label={`${brandLabel(c.brand)} •••• ${c.last4}`}
                />
              ))}
            </RadioGroup>
          ) : (
            <Typography color='text.secondary'>
              You have no other saved cards. Add a new card using the &quot;Add
              card&quot; button above, then return here to replace this card.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => handleCloseReplace()}
            sx={{
              position: 'relative',
              minWidth: 100,
              marginLeft: '10px',
              borderColor: getColor(companyTheme),
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: getColor(companyTheme),
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          {replaceDialog.otherCards.length > 0 && (
            <Button
              variant='contained'
              onClick={handleReplaceWithExisting}
              disabled={submitting || !replaceDialog.replaceWithId}
              sx={{
                backgroundColor: getColor(companyTheme),
                '&:hover': { backgroundColor: companyTheme?.primary?.dark },
              }}
            >
              Replace card
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Modal open={editDialog.open} onClose={() => handleClose()}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
            minWidth: 300,
            maxWidth: 600,
            maxHeight: 'calc(100vh - 200px)',
            overflow: 'auto',
            ...customScrollbarStyle,
          }}
        >
          <AppCard
            title={
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: Fonts?.BOLD,
                  color: getColor(companyTheme),
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: Fonts.BOLD,
                    fontSize: 18,
                  }}
                >
                  Edit card address
                </Typography>

                <IconButton onClick={() => handleClose()} aria-label='close'>
                  <CloseIcon />
                </IconButton>
              </Box>
            }
          >
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {editDialog.card &&
                `${brandLabel(editDialog.card.brand)} •••• ${
                  editDialog.card.last4
                }`}
            </Typography>
            <AppGridContainer>
              <Grid size={{ xs: 12, md: 12 }}>
                <TextField
                  label='Address'
                  value={editDialog.address}
                  onChange={(e) =>
                    setEditDialog((p) => ({ ...p, address: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl sx={{ width: '100%', borderRadius: 50 }}>
                  <TextField
                    sx={{ width: '100%' }}
                    select
                    name='country'
                    value={editDialog.country}
                    label='Country'
                    onChange={(e) => {
                      setEditDialog((p) => ({ ...p, country: e.target.value }));
                    }}
                  >
                    <MenuItem value='us'>US</MenuItem>
                    <MenuItem value='ca'>CA</MenuItem>
                  </TextField>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  id='add-card-state-autocomplete'
                  options={states}
                  getOptionLabel={(option) =>
                    `${option.name} - ${option.abbreviation}`
                  }
                  value={
                    states?.find((s) => s.abbreviation === editDialog.state) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setEditDialog((p) => ({
                      ...p,
                      state: newValue ? newValue.abbreviation : '',
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label='State' variant='outlined' />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label='City'
                  value={editDialog.city}
                  onChange={(e) =>
                    setEditDialog((p) => ({ ...p, city: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label='Zip Code'
                  value={editDialog.zip_code}
                  onChange={(e) =>
                    setEditDialog((p) => ({ ...p, zip_code: e.target.value }))
                  }
                  fullWidth
                />
              </Grid>
            </AppGridContainer>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
                pt: 5,
              }}
            >
              <Button
                type='submit'
                variant='contained'
                onClick={handleSaveEdit}
                disabled={submitting}
                sx={{
                  position: 'relative',
                  width: '100%',
                  backgroundColor: companyTheme
                    ? companyTheme.primary.main
                    : '#2196f3',
                  '&:hover': {
                    backgroundColor: companyTheme
                      ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                      : alpha('#2196f3', 0.8),
                  },
                  // 👇 Add this block to control disabled appearance
                  '&.Mui-disabled': {
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    color: '#fff', // Optional: keep text visible
                    opacity: 0.6, // Optional: slight dim to indicate disabled state
                  },
                }}
              >
                Update Address
              </Button>
            </Box>
          </AppCard>
        </Box>
      </Modal>
      <AppInfoView />
      {addCardOpen && (
        <AddCardForm
          isOpen={addCardOpen}
          onClose={() => setAddCardOpen(false)}
          onSuccess={fetchCards}
          fetchError={fetchError}
          showMessage={showMessage}
          userId={user?.id}
        />
      )}
    </Box>
  );
};

export default MyWallet;
